import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type Announcements,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

import { useLabels, type Labels } from '../../i18n/useLabels';
import { LABEL_KEYS } from '../../viewmodels/messages';
import { DragPreview } from './DragPreview';
import { HandleRegistryContext, type HandleRegistry } from './handleRegistry';

export type SortableItem = {
  id: string;
  name: string;
};

type Props = {
  items: SortableItem[];
  canReorder: boolean;
  onReorder: (activeId: string, overId: string) => void;
  children: ReactNode;
};

type AnnouncementKey =
  | 'reorderPickedUp'
  | 'reorderMoved'
  | 'reorderDropped'
  | 'reorderCancelled';

function createAnnouncements(
  items: SortableItem[],
  formatMessage: Labels['formatMessage']
): Announcements {
  const total = items.length;
  // The library reports the picked-up item as "over" itself right after the pick up. That
  // would replace the pick up announcement.
  let isAwaitingFirstOver = false;

  const announce = (
    key: AnnouncementKey,
    activeId: string | number,
    positionId: string | number
  ) => {
    const active = items.find(item => item.id === activeId);
    const position = items.findIndex(item => item.id === positionId) + 1;

    return formatMessage({
      key: LABEL_KEYS[key],
      params: { name: active?.name ?? '', position, total },
    });
  };

  return {
    onDragStart: ({ active }) => {
      isAwaitingFirstOver = true;

      return announce('reorderPickedUp', active.id, active.id);
    },
    onDragOver: ({ active, over }) => {
      const isFirstOver = isAwaitingFirstOver;

      isAwaitingFirstOver = false;

      if (!over || (isFirstOver && over.id === active.id)) {
        return undefined;
      }

      return announce('reorderMoved', active.id, over.id);
    },
    onDragEnd: ({ active, over }) =>
      announce('reorderDropped', active.id, over?.id ?? active.id),
    onDragCancel: ({ active }) =>
      announce('reorderCancelled', active.id, active.id),
  };
}

function ReorderableList({
  items,
  onReorder,
  children,
}: Omit<Props, 'canReorder'>) {
  const { formatMessage } = useLabels();
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [focusRequestId, setFocusRequestId] = useState<string | null>(null);
  const handles = useRef(new Map<string, HTMLElement>());

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const registry = useMemo<HandleRegistry>(
    () => ({
      register: (id, element) => {
        if (element) {
          handles.current.set(id, element);
        } else {
          handles.current.delete(id);
        }
      },
    }),
    []
  );

  const itemIds = useMemo(() => items.map(item => item.id), [items]);
  const announcements = useMemo(
    () => createAnnouncements(items, formatMessage),
    [items, formatMessage]
  );

  // Moving a row in the DOM can drop its focus, so focus is set once the new order has rendered
  useEffect(() => {
    if (focusRequestId === null) {
      return;
    }

    handles.current.get(focusRequestId)?.focus();
    setFocusRequestId(null);
  }, [focusRequestId]);

  const handleDragStart = useCallback(({ active }: DragStartEvent) => {
    setDraggingId(String(active.id));
  }, []);

  const handleDragEnd = useCallback(
    ({ active, over, activatorEvent }: DragEndEvent) => {
      const activeId = String(active.id);

      setDraggingId(null);

      if (activatorEvent.type === 'keydown') {
        setFocusRequestId(activeId);
      }

      if (over && over.id !== active.id) {
        onReorder(activeId, String(over.id));
      }
    },
    [onReorder]
  );

  const handleDragCancel = useCallback(() => setDraggingId(null), []);

  const draggedItem = items.find(item => item.id === draggingId);

  return (
    <HandleRegistryContext.Provider value={registry}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
        accessibility={{
          announcements,
          screenReaderInstructions: {
            draggable: formatMessage({
              key: LABEL_KEYS.reorderInstructions,
            }),
          },
          // The announcements and the preview stay out of the table markup. The list
          // restores focus itself.
          container: document.body,
          restoreFocus: false,
        }}
      >
        <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
          {children}
        </SortableContext>
        {createPortal(
          <DragOverlay>
            {draggedItem && <DragPreview name={draggedItem.name} />}
          </DragOverlay>,
          document.body
        )}
      </DndContext>
    </HandleRegistryContext.Provider>
  );
}

// With nothing to reorder the list renders its children alone, so the page stays as it
// is today.
export function SortableList({ canReorder, children, ...props }: Props) {
  if (!canReorder) {
    return children;
  }

  return <ReorderableList {...props}>{children}</ReorderableList>;
}
